import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import {
  mainnet,
  bsc,
  polygon,
  avalanche,
  celo,
  sepolia,
  goerli,
  bscTestnet,
  AppKitNetwork,
} from '@reown/appkit/networks';
import { AppKit, createAppKit } from '@reown/appkit/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { DefaultRootState, useDispatch, useSelector } from 'react-redux';
import { WagmiProvider } from 'wagmi';

import {
  Web3ConnectSuccessPopupDefault,
  Web3ConnectSuccessPopupForPresale,
} from 'common/components/Web3/popups/ConnectSuccess/ConnectSuccess.component';

import { usePopup } from 'common/hooks';
import { userActions } from 'common/redux/actions';

import { routes } from 'routes/routes.constants';

import siweConfig from './siwe.config';

const queryClient = new QueryClient();

const isLocalhost = window.location.hostname === 'localhost';
const isDev = window.location.hostname === 'www.xbo-dev.space-app.io';

const projectId = isLocalhost || isDev ? 'e8e580a78f108029f1ccdd08a6be3e10' : '1d67f9feafe8d888415784ad8cdfac1f';

const metadata = {
  name: 'xbo.com',
  description: 'Cryptocurrency Exchange to Buy & Sell Crypto',
  url: window.location.origin,
  icons: ['https://assets.xbo.com/token-icons/svg/XBO.svg'],
};

const networks: [AppKitNetwork, ...AppKitNetwork[]] = [
  mainnet,
  bsc,
  polygon,
  avalanche,
  celo,
  sepolia,
  goerli,
  bscTestnet,
];
const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId,
});

const createAppKitConfig = (params = {}) =>
  createAppKit({
    adapters: [wagmiAdapter],
    networks,
    defaultNetwork: mainnet,
    projectId,
    metadata,
    debug: true,
    themeMode: 'light',
    allowUnsupportedChain: false,
    allWallets: 'HIDE',
    themeVariables: {
      '--w3m-z-index': 99999,
    },
    showWallets: true,
    featuredWalletIds: [
      'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // metamask
      '4622a2b2d6af1c9844944291e5e7351a6aa24cd7b23099efac1b2fd875da31a0', // trastwallet
    ],
    features: {
      emailShowWallets: false,
      socials: false,
      email: false,
      analytics: false,
      swaps: false,
      onramp: false,
    },
    ...params,
  });

function Web3Provider({ children }: { children: React.ReactNode }) {
  const { show } = usePopup();
  const dispatch = useDispatch();

  const { isAuthenticated, loading } = useSelector((state: DefaultRootState) => state.common.user);

  const [appkit, setAppkit] = useState<AppKit | undefined>();

  useEffect(() => {
    dispatch(userActions.actionGetCurrentUser());
  }, [dispatch]);

  useEffect(() => {
    if (loading) return;
    const _appkit = createAppKitConfig({
      siweConfig: isAuthenticated ? undefined : siweConfig,
    });

    setAppkit(_appkit);

    _appkit?.subscribeEvents(({ data }) => {
      const isTokenPresale = window.location.pathname === routes.getTokenPresalePage();
      const isTokenPresaleConnected = isTokenPresale && data.event === 'SIWX_AUTH_SUCCESS';
      const isMainConnected = !isTokenPresale && data.event === 'CONNECT_SUCCESS';

      if (isTokenPresaleConnected) show(Web3ConnectSuccessPopupForPresale());
      if (isMainConnected) show(Web3ConnectSuccessPopupDefault());
    });
  }, [isAuthenticated, loading, show]);

  if (!appkit) return null;

  return (
    <WagmiProvider config={wagmiAdapter.wagmiConfig}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </WagmiProvider>
  );
}

export default Web3Provider;
